import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AvailityVOBAErrorMessages } from '@finpay-development/shared-platform/lib/esm/shared/enums';
import { showErrorStatus } from '@finpay-development/shared-platform/lib/esm/security/state/user-slice';
import {
  createSelectedVob,
  loadPatientVobList,
} from '@finpay-development/shared-platform/lib/esm/shared/state/vob-thunk';
import { ClientVobStatusCardViewModel } from '@finpay-development/shared-platform/lib/esm/shared/model/client-vob-status-card';
import { PayerRow } from '@finpay-development/shared-platform/lib/esm/admin-configuration/models/payers';
import { VobSource } from '@finpay-development/shared-platform/lib/esm/meta-data/models/metaData';
import { AppDispatch } from '../../../../../shared/state/store';
import { RootState } from '../../../../../shared/state/root-reducer';
import { PatientFormValues } from '../types';
import {
  findFirstFacilityWithValidNpi,
  formatAvailityDob,
  getAvailityVobWithOverrides,
} from '../utils/availity-vob';

function isAvailityVobConfigVisible(
  clientId: number,
  facilityId: number,
  allClientVobs: ClientVobStatusCardViewModel[] | null | undefined
): boolean {
  if (!clientId || !facilityId || !allClientVobs) {
    return false;
  }

  const clientVobsForPatient = allClientVobs.filter(
    (clientVob) => clientVob.clientId === Number(clientId)
  );

  const facilityExistsInClientVobs = clientVobsForPatient.some(
    (clientVob) => Number(clientVob.clientFacilityId) === facilityId
  );

  if (facilityExistsInClientVobs) {
    return !!clientVobsForPatient.find(
      (clientVob) =>
        Number(clientVob.clientFacilityId) === facilityId &&
        clientVob.vobTypeId === 3 &&
        clientVob.isIntEnabled === true
    );
  }

  return !!clientVobsForPatient.find(
    (clientVob) =>
      clientVob.vobTypeId === 3 &&
      clientVob.isIntEnabled === true &&
      clientVob.clientFacilityId == null
  );
}

function getAvailityVOBErrorMessage(response: any): string {
  const message = response?.entity?.message;
  if (typeof message === 'string' && message.trim().length > 0) {
    return message;
  }
  return AvailityVOBAErrorMessages.GENERIC_ERROR;
}

/** Availity payer code (externalCode) for the selected master payor. */
function getAvailityPcodeForPayor(
  payorId: number,
  payersMap: Map<number, PayerRow> | null | undefined,
  vobSources: VobSource[] | null | undefined
): string {
  const availityVobSource = (vobSources ?? []).find(
    (source) => source.slug === 'availity'
  );
  if (!availityVobSource) {
    return '';
  }
  const payor = payersMap?.get(payorId);
  const availityPayorSource = payor?.payorSource?.find(
    (source) => source.vobSourceId === availityVobSource.vobSourceId
  );
  return availityPayorSource?.externalCode != null
    ? String(availityPayorSource.externalCode).trim()
    : '';
}

function normalizeDob(raw: string | null | undefined): string {
  return formatAvailityDob(raw);
}

function hasDemographicsChanged(
  formValues: PatientFormValues,
  selectedPatient: {
    contact?: {
      firstName?: string;
      lastName?: string;
      birthDate?: string | null;
    };
  } | null
): boolean {
  const formDob = normalizeDob(formValues.dateOfBirth);
  const savedDob = normalizeDob(selectedPatient?.contact?.birthDate);
  return (
    (formValues.firstName || '').trim() !==
      (selectedPatient?.contact?.firstName || '').trim() ||
    (formValues.lastName || '').trim() !==
      (selectedPatient?.contact?.lastName || '').trim() ||
    formDob !== savedDob
  );
}

function hasInsuranceChanged(
  formValues: PatientFormValues,
  savedInsurance: {
    payorId?: number;
    payorPlanId?: number;
    memberId?: string;
    groupNumber?: string;
    subscriberName?: string;
    subscriberRelation?: number;
  } | null
): boolean {
  return (
    Number(formValues.payorId) !== Number(savedInsurance?.payorId || 0) ||
    Number(formValues.payorPlanId) !== Number(savedInsurance?.payorPlanId || 0) ||
    (formValues.memberId || '').trim() !== (savedInsurance?.memberId || '').trim() ||
    (formValues.groupNumber || '').trim() !==
      (savedInsurance?.groupNumber || '').trim() ||
    (formValues.subscriberName || '').trim() !==
      (savedInsurance?.subscriberName || '').trim() ||
    Number(formValues.subscriberRelation || 0) !==
      Number(savedInsurance?.subscriberRelation || 0)
  );
}

export function useRunAvailityVob(
  formValues: PatientFormValues,
  saveFormChanges: () => Promise<boolean>
) {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const allClientsWithFacilitiesMap = useSelector(
    (state: RootState) =>
      state.implementationContext?.implementationSpecialistClient
        .allClientsWithFacilitiesMap
  );
  const allClientsMap = useSelector(
    (state: RootState) =>
      state.implementationContext?.implementationSpecialistClient.allClientsMap
  );
  const allClientVobs = useSelector(
    (state: RootState) =>
      state.implementationContext?.implementationSpecialistClient.allClientVobs
  );
  const payersMap = useSelector(
    (state: RootState) => state.adminContext.adminConfigurationContext?.payersMap
  );
  const vobSources = useSelector(
    (state: RootState) => state.metaData.vobSource
  );
  const selectedPatient = useSelector(
    (state: RootState) => state.patientContext.selectedPatient
  );
  const selectedPatientCurrentInsurance = useSelector(
    (state: RootState) => state.patientContext.selectedPatientCurrentInsurance
  );

  const [isRunningAvailityVOB, setIsRunningAvailityVOB] = useState(false);
  const [showUpdateInsuranceModal, setShowUpdateInsuranceModal] = useState(false);
  const [isSavingInsurance, setIsSavingInsurance] = useState(false);
  const [pendingVobId, setPendingVobId] = useState<number | null>(null);

  const clientId = formValues.clientId ? Number(formValues.clientId) : 0;
  const patientId = Number(formValues.patientId) || 0;

  /** First Redux facility — used for Availity visibility (same as VOB default). */
  const visibilityFacilityId = useMemo(() => {
    if (!clientId) {
      return 0;
    }
    const client =
      allClientsWithFacilitiesMap?.get(clientId) ??
      allClientsMap?.get(clientId);
    const first = client?.clientFacilities?.[0];
    const id = Number(first?.facilityId);
    return Number.isInteger(id) && id > 0 ? id : 0;
  }, [clientId, allClientsWithFacilitiesMap, allClientsMap]);

  const clientFacilities = useMemo(() => {
    if (!clientId) {
      return [];
    }
    const client =
      allClientsWithFacilitiesMap?.get(clientId) ??
      allClientsMap?.get(clientId);
    return client?.clientFacilities ?? [];
  }, [clientId, allClientsWithFacilitiesMap, allClientsMap]);

  const showRunAvailityButton =
    patientId > 0 &&
    visibilityFacilityId > 0 &&
    isAvailityVobConfigVisible(clientId, visibilityFacilityId, allClientVobs);

  const navigateToSavedVob = useCallback(
    (vobId: number) => {
      navigate(`/patients/${patientId}/vob/${vobId}`);
    },
    [navigate, patientId]
  );

  const finishAfterInsurancePrompt = useCallback(
    async (shouldUpdate: boolean) => {
      if (pendingVobId == null || !patientId) {
        setShowUpdateInsuranceModal(false);
        setPendingVobId(null);
        return;
      }

      if (shouldUpdate) {
        setIsSavingInsurance(true);
        try {
          const saved = await saveFormChanges();
          if (!saved) {
            setIsSavingInsurance(false);
            return;
          }
        } catch (error) {
          console.error('Failed to update patient/insurance after VOB:', error);
          dispatch(showErrorStatus('Failed to update patient information'));
          setIsSavingInsurance(false);
          return;
        }
        setIsSavingInsurance(false);
      }

      const vobId = pendingVobId;
      setShowUpdateInsuranceModal(false);
      setPendingVobId(null);
      navigateToSavedVob(vobId);
    },
    [
      dispatch,
      navigateToSavedVob,
      patientId,
      pendingVobId,
      saveFormChanges,
    ]
  );

  const onUpdateInsuranceYes = useCallback(() => {
    void finishAfterInsurancePrompt(true);
  }, [finishAfterInsurancePrompt]);

  const onUpdateInsuranceNo = useCallback(() => {
    void finishAfterInsurancePrompt(false);
  }, [finishAfterInsurancePrompt]);

  const runAvailityVob = useCallback(async () => {
    const missingFields: string[] = [];
    const memberId = formValues.memberId || '';
    const dob = formatAvailityDob(formValues.dateOfBirth);

    if (!memberId.trim()) {
      missingFields.push('Member ID');
    }
    if (!formValues.firstName?.trim()) {
      missingFields.push('First Name');
    }
    if (!formValues.lastName?.trim()) {
      missingFields.push('Last Name');
    }
    if (!dob) {
      missingFields.push('Date of Birth');
    }
    if (!formValues.payorId) {
      missingFields.push('Current Insurance Payer');
    }
    if (!formValues.payorPlanId) {
      missingFields.push('Current Insurance Plan');
    }

    if (missingFields.length > 0) {
      dispatch(
        showErrorStatus(
          `The following fields are required to run Availity VOB: ${missingFields.join(', ')}`
        )
      );
      return;
    }

    if (!patientId) {
      dispatch(showErrorStatus('Patient is required to run Availity VOB'));
      return;
    }

    const pcode = getAvailityPcodeForPayor(
      Number(formValues.payorId),
      payersMap,
      vobSources
    );
    if (!pcode) {
      dispatch(
        showErrorStatus(
          'Selected payer has no Availity payor code configured'
        )
      );
      return;
    }

    setIsRunningAvailityVOB(true);
    try {
      const facilityWithNpi = await findFirstFacilityWithValidNpi(clientFacilities);
      if (!facilityWithNpi) {
        dispatch(
          showErrorStatus(
            'No client facility with a valid NPI was found'
          )
        );
        return;
      }

      const responseFromAvaility = await getAvailityVobWithOverrides(
        patientId,
        facilityWithNpi.facilityId,
        {
          fname: formValues.firstName,
          lname: formValues.lastName,
          dob,
          memberId: formValues.memberId,
          pcode,
          npi: facilityWithNpi.npi,
        }
      );

      if (responseFromAvaility.hasErrors) {
        dispatch(showErrorStatus(getAvailityVOBErrorMessage(responseFromAvaility)));
        return;
      }

      if (responseFromAvaility.entity?.activePolicy === false) {
        navigate(`/patients/${patientId}/vob`);
        dispatch(showErrorStatus("Patient's insurance policy is not active"));
        return;
      }

      // Availity maps payor/plan from stored patient insurance; overlay form selection
      // (pcode override only affects the eligibility call, not the returned entity).
      const selectedPayorId = Number(formValues.payorId);
      const selectedPlanId = Number(formValues.payorPlanId);
      const masterPayor = payersMap?.get(selectedPayorId);
      const masterPlan = masterPayor?.payorPlans?.find(
        (plan) => Number(plan.payorPlanId) === selectedPlanId
      );
      const vobToSave = {
        ...responseFromAvaility.entity,
        payor: {
          ...(responseFromAvaility.entity?.payor ?? {}),
          payorId: selectedPayorId,
          ...(masterPayor?.payorName
            ? {payorName: masterPayor.payorName}
            : {}),
        },
        plan: {
          ...(responseFromAvaility.entity?.plan ?? {}),
          payorPlanId: selectedPlanId,
          ...(masterPlan?.planName ? {planName: masterPlan.planName} : {}),
        },
      };

      const response = await dispatch(createSelectedVob(vobToSave));
      if (response.payload) {
        await dispatch(loadPatientVobList(patientId));
        const vobId = (response.payload as { vobId: number }).vobId;
        const demographicsChanged = hasDemographicsChanged(
          formValues,
          selectedPatient
        );
        const insuranceChanged = hasInsuranceChanged(
          formValues,
          selectedPatientCurrentInsurance
        );

        if (!demographicsChanged && !insuranceChanged) {
          navigateToSavedVob(vobId);
          return;
        }

        setPendingVobId(vobId);
        setShowUpdateInsuranceModal(true);
      }
    } catch (error) {
      console.error('Failed to run Availity VOB:', error);
      dispatch(showErrorStatus('Failed to run Availity VOB'));
    } finally {
      setIsRunningAvailityVOB(false);
    }
  }, [
    clientFacilities,
    dispatch,
    formValues,
    navigate,
    navigateToSavedVob,
    patientId,
    payersMap,
    selectedPatient,
    selectedPatientCurrentInsurance,
    vobSources,
  ]);

  return {
    showRunAvailityButton,
    isRunningAvailityVOB,
    runAvailityVob,
    showUpdateInsuranceModal,
    isSavingInsurance,
    onUpdateInsuranceYes,
    onUpdateInsuranceNo,
  };
}
